/**
 * useAuth - Redux-based auth hook
 * Same API as old AuthContext - drop-in replacement for fast Redux reads
 */
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  loginUser,
  logoutUser,
  refreshPermissions,
  checkAuth,
} from '../store/slices/authSlice';

export const useAuth = () => {
  const dispatch = useDispatch();
  const { user, permissions, isAuthenticated, loading, permissionsReady } = useSelector(
    (state) => state.auth
  );

  const isSuperAdmin = permissions.some(
    (p) => (p || '').toLowerCase().replace(/\s+/g, '-') === 'super-admin'
  );

  const hasPermission = useCallback(
    (perm) => isSuperAdmin || permissions.includes(perm),
    [permissions, isSuperAdmin]
  );
  const hasAnyPermission = useCallback(
    (perms) => isSuperAdmin || perms.some((p) => permissions.includes(p)),
    [permissions, isSuperAdmin]
  );

  const login = useCallback(
    (credentials) => dispatch(loginUser(credentials)),
    [dispatch]
  );

  const logout = useCallback(() => dispatch(logoutUser()), [dispatch]);

  const refreshPermissionsFn = useCallback(
    () => dispatch(refreshPermissions()),
    [dispatch]
  );

  const initAuth = useCallback(() => dispatch(checkAuth()), [dispatch]);

  return {
    user,
    permissions,
    isAuthenticated,
    loading,
    permissionsReady,
    login,
    logout,
    refreshPermissions: refreshPermissionsFn,
    hasPermission,
    hasAnyPermission,
    initAuth,
  };
};
